import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { OpsFeatureFlags } from "@/components/admin/OpsFeatureFlags";
import { useOpsFlags } from "@/hooks/useOpsFlags";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  advanceOrderStatus,
  listKitchenBoard,
  type KitchenOrder,
} from "@/lib/admin/ops.functions";
import {
  KITCHEN_ADVANCE,
  KITCHEN_COLUMNS,
  OPS_STATUS_LABEL,
  type KitchenColumn,
  type OpsOrderStatus,
} from "@/lib/admin/ops.schemas";

export const Route = createFileRoute("/_admin/admin/kitchen")({
  head: () => ({
    meta: [
      { title: "Kitchen Board — Admin" },
      {
        name: "description",
        content: "Live kitchen workflow board for accepted, preparing, ready and out-for-delivery orders.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: KitchenPage,
});

const REFRESH_MS = 30_000;

function KitchenPage() {
  const { flags, isLoading: flagsLoading } = useOpsFlags();
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [target, setTarget] = useState<{
    order: KitchenOrder;
    to: OpsOrderStatus;
  } | null>(null);

  const listFn = useServerFn(listKitchenBoard);
  const enabled = flags.kitchen_view_enabled;

  const board = useQuery({
    queryKey: ["admin", "kitchen", search, dateFrom, dateTo],
    queryFn: () =>
      listFn({
        data: {
          search,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        },
      }),
    enabled,
    refetchInterval: enabled ? REFRESH_MS : false,
    placeholderData: (prev) => prev,
  });

  const columns = useMemo(() => {
    const map: Record<string, KitchenOrder[]> = {
      accepted: [],
      preparing: [],
      ready: [],
      out_for_delivery: [],
    };
    for (const o of board.data ?? []) {
      // New / confirmed orders wait in the Accepted column until accepted.
      const key = o.status === "new" || o.status === "confirmed" ? "accepted" : o.status;
      if (map[key]) map[key]!.push(o);
    }
    return map;
  }, [board.data]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Kitchen"
        description="Live workflow board. Advance orders one controlled step at a time."
      />

      {flagsLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !enabled ? (
        <div className="space-y-6">
          <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            The kitchen board is turned off. Enable{" "}
            <span className="font-mono">KITCHEN_VIEW_ENABLED</span> below to use it.
          </div>
          <OpsFeatureFlags />
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order ref, customer, phone or plan"
              aria-label="Search kitchen orders"
            />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Delivery date from"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="Delivery date to"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Auto-refreshing every 30 seconds
            {board.isFetching ? " · refreshing…" : ""}
          </p>

          {board.isLoading ? (
            <div className="grid gap-4 lg:grid-cols-4">
              {KITCHEN_COLUMNS.map((c) => (
                <Skeleton key={c} className="h-64 w-full" />
              ))}
            </div>
          ) : board.error ? (
            <p className="text-sm text-destructive">
              Couldn't load the board. Please try again.
            </p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-4">
              {KITCHEN_COLUMNS.map((col) => (
                <KitchenColumnView
                  key={col}
                  column={col}
                  orders={columns[col] ?? []}
                  onAdvance={(order) =>
                    setTarget({ order, to: KITCHEN_ADVANCE[col] })
                  }
                  lifecycleEnabled={flags.order_lifecycle_enabled}
                />
              ))}
            </div>
          )}

          <OpsFeatureFlags />
        </>
      )}

      <AdvanceDialog target={target} onClose={() => setTarget(null)} />
    </div>
  );
}

function KitchenColumnView({
  column,
  orders,
  onAdvance,
  lifecycleEnabled,
}: {
  column: KitchenColumn;
  orders: KitchenOrder[];
  onAdvance: (order: KitchenOrder) => void;
  lifecycleEnabled: boolean;
}) {
  return (
    <section className="rounded-lg border bg-card">
      <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b px-3 py-2">
        <h2 className="truncate text-sm font-semibold">
          {OPS_STATUS_LABEL[column]}
        </h2>
        <Badge variant="secondary" className="shrink-0">
          {orders.length}
        </Badge>
      </header>
      <div className="space-y-3 p-3">
        {orders.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing here.</p>
        ) : (
          orders.map((o) => (
            <article key={o.id} className="rounded-md border p-3 text-sm">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                <span className="truncate font-mono text-xs">{o.order_ref}</span>
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  ×{o.quantity}
                </Badge>
              </div>
              <div className="mt-1 truncate font-medium">{o.customer_name}</div>
              <div className="truncate text-xs text-muted-foreground">
                {o.meal_plan_title}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {o.preferred_delivery_date} · {o.preferred_delivery_time?.slice(0, 5) ?? "--:--"}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {o.neighbourhood}
              </div>
              {o.special_instructions ? (
                <p className="mt-2 rounded bg-muted/50 p-2 text-xs">
                  {o.special_instructions}
                </p>
              ) : null}
              <Button
                size="sm"
                className="mt-3 w-full"
                disabled={!lifecycleEnabled}
                onClick={() => onAdvance(o)}
              >
                Mark {OPS_STATUS_LABEL[KITCHEN_ADVANCE[column]]}
              </Button>
              {!lifecycleEnabled ? (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Enable ORDER_LIFECYCLE_ENABLED to advance orders.
                </p>
              ) : null}
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function AdvanceDialog({
  target,
  onClose,
}: {
  target: { order: KitchenOrder; to: OpsOrderStatus } | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const advanceFn = useServerFn(advanceOrderStatus);
  const [note, setNote] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      if (!target) return;
      await advanceFn({
        data: { id: target.order.id, to: target.to, note, force: false },
      });
    },
    onSuccess: () => {
      toast.success("Status updated");
      queryClient.invalidateQueries({ queryKey: ["admin", "kitchen"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
      setNote("");
      onClose();
    },
    onError: (err: Error) => {
      if (err.message === "illegal_transition") {
        toast.error("That status change isn't allowed from the current status.");
      } else if (err.message === "lifecycle_disabled") {
        toast.error("Order lifecycle is turned off.");
      } else {
        toast.error("Couldn't update the status.");
      }
    },
  });

  return (
    <Dialog open={target !== null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Move to {target ? OPS_STATUS_LABEL[target.to] : ""}
          </DialogTitle>
          <DialogDescription>
            {target
              ? `${target.order.order_ref} — ${target.order.customer_name}. The customer is notified automatically when this status is configured for notifications.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for the timeline (e.g. packed early)"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Updating…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
